import {
  useCallback,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Yup from 'yup';
import clsx from 'clsx';
import {
  textFieldValidation,
  numberFieldValidation,
  addonValidationSchema,
} from '@/utils/validations';
import { Modal } from '@/components';
import PriceInput from '@/components/inputControls/PriceInput';
import { ModalProps } from '@mui/base/Modal';
import {
  AddonVatRegime,
  IAddonData,
  PriceUnit,
} from '@/types/addon-management.types';
import { useGetHarborsQuery } from '@/services/HarborManagementService';
import { HarborResponse } from '@/types/harbor-management.types';
import { isProductVatConfigActive } from '@/pages/product-vat-config/productVatConfigUtils';
import { enqueueSnackbar } from 'notistack';
import { RootState } from '@/store/store';
import { useSelector } from 'react-redux';
import { FacilityCondition, amenityConditionOptions } from '@/constant';
import { useIntl } from 'react-intl';
import { I18N_LANGUAGES } from '@/i18n';
import {
  useGetAddonVatRegimeByIdQuery,
  useGetAddonVatRegimesQuery,
  useTranslateAddonMutation,
} from '@/services/AddonManagementService';
import AddonPreviewModal from './AddonPreviewModal';
import NumberInput from '@/components/inputControls/NumberInput';
import { formatDisplayDate } from '@/utils/Date';
import {
  AddonAvailabilityDateRanges,
  MAX_ADDON_AVAILABILITY_DATE_RANGES,
} from './AddonAvailabilityDateRanges';
import { AddonAvailabilityDateRange } from '@/types/addon-management.types';

function addonUsesSecretHandover(
  mode: string | null | undefined
): mode is 'KEY_BOX' | 'ACCESS_CODE' {
  return mode === 'KEY_BOX' || mode === 'ACCESS_CODE';
}

interface HarborOption {
  id: string;
  name: string;
  countryShortCode?: string;
}

interface HTMLInputElementWithPreventWheel extends HTMLInputElement {
  _preventWheel?: (event: WheelEvent) => void;
}

interface AddonModalProps extends Omit<ModalProps, 'children'> {
  mode: 'view' | 'edit' | 'create' | 'details';
  formData: Partial<IAddonData>;
  setFormData: Dispatch<SetStateAction<Partial<IAddonData>>>;
  errors: { [key: string]: string };
  setErrors: Dispatch<SetStateAction<{ [key: string]: string }>>;
  onSubmit: () => Promise<void>;
  isSubmitting?: boolean;
}

type AddonTranslationMap = NonNullable<IAddonData['translations']>;

const hasTranslations = (
  value?: IAddonData['translations']
): value is AddonTranslationMap =>
  Boolean(value && Object.keys(value).length > 0);

const validateAvailabilityDateRanges = (
  ranges?: AddonAvailabilityDateRange[] | null
): string | undefined => {
  if (!ranges || ranges.length === 0) return undefined;
  if (ranges.length > MAX_ADDON_AVAILABILITY_DATE_RANGES) {
    return `You can configure at most ${MAX_ADDON_AVAILABILITY_DATE_RANGES} availability ranges`;
  }

  const normalized = ranges.map((range) => ({
    start_date: range.start_date?.trim() || '',
    end_date: range.end_date?.trim() || '',
  }));

  for (const range of normalized) {
    if (!range.start_date || !range.end_date) {
      return 'Each availability range must include a start and end date';
    }
    if (range.end_date < range.start_date) {
      return 'Availability end date must be on or after the start date';
    }
  }

  const sorted = [...normalized].sort((a, b) =>
    a.start_date.localeCompare(b.start_date)
  );
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].start_date <= sorted[i - 1].end_date) {
      return 'Availability ranges cannot overlap';
    }
  }

  return undefined;
};

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return typeof error === 'string' ? error : undefined;
  }

  const maybeError = error as {
    data?: unknown;
    message?: unknown;
    error?: unknown;
  };

  if (maybeError.data) {
    if (typeof maybeError.data === 'string') return maybeError.data;

    if (typeof maybeError.data === 'object') {
      const data = maybeError.data as {
        message?: unknown;
        error?: unknown;
      };

      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
    }
  }

  if (typeof maybeError.message === 'string') return maybeError.message;
  if (typeof maybeError.error === 'string') return maybeError.error;

  return undefined;
};

const AddonModal = ({
  mode,
  formData,
  setFormData,
  errors,
  setErrors,
  onSubmit,
  isSubmitting,
  open,
  onClose,
  className,
}: AddonModalProps) => {
  const intl = useIntl();
  const [translateAddon, { isLoading: isTranslatingAddon }] =
    useTranslateAddonMutation();
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [showPreviewNotice, setShowPreviewNotice] = useState(false);
  const [formDataSnapshot, setFormDataSnapshot] = useState<
    Pick<
      Partial<IAddonData>,
      | 'name'
      | 'description'
      | 'access_handover_mode'
      | 'key_box_pickup_instructions'
      | 'key_box_return_instructions'
    >
  >({});
  const [translationsValid, setTranslationsValid] = useState(false);
  const harborDropdownRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [harborOptions, setHarborOptions] = useState<HarborOption[]>([]);
  const [isHarborDropdownOpen, setIsHarborDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const selectedHarborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );
  const isHarborDetailsRoute = location.pathname === '/harbors/details';

  const previewInitialTranslations = useMemo(
    () => formData.translations || {},
    [formData.translations]
  );

  useEffect(() => {
    if (selectedHarborId && isHarborDetailsRoute) {
      const selectedHarbor = harborOptions.find(
        (h) => h.id === selectedHarborId
      );
      if (selectedHarbor) {
        setFormData((prev) => ({
          ...prev,
          harbor_id: mode === 'create' ? selectedHarborId : selectedHarbor.id,
          harbor: {
            id: selectedHarbor.id,
            name: selectedHarbor.name,
          },
        }));
      }
    }
  }, [
    selectedHarborId,
    setFormData,
    harborOptions,
    isHarborDetailsRoute,
    mode,
  ]);

  // Helper function to convert time string to minutes for comparison
  const timeToMinutes = useCallback((timeString: string): number => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  }, []);

  const validateField = useCallback(
    async (name: keyof IAddonData | string, value: unknown) => {
      try {
        const schema = Yup.object().shape({
          name: textFieldValidation('Addon Name', 2, 50, true),
          description: textFieldValidation('Description', 0, 500, false),
          price: numberFieldValidation('Price', 0, 10000, true),
          is_available: Yup.boolean().notRequired(),
          is_standalone: Yup.boolean().notRequired(),
          quantity: numberFieldValidation('Quantity', 0, 1000, true),
          condition: Yup.string().notRequired(),
          price_unit: Yup.string().required('Price unit is required'),
          start_time: Yup.string().when('price_unit', {
            is: (value: string) => value === PriceUnit.PER_HOUR,
            then: (schema) =>
              schema
                .required('Start time is required')
                .test(
                  'is-before-end-time',
                  'Start time must be before end time',
                  function (startTime) {
                    const endTime = this.parent.end_time;
                    if (
                      !startTime ||
                      !endTime ||
                      this.parent.price_unit !== PriceUnit.PER_HOUR
                    ) {
                      return true;
                    }
                    // Convert time strings to minutes for proper comparison
                    const startMinutes = timeToMinutes(startTime);
                    const endMinutes = timeToMinutes(endTime);
                    return startMinutes < endMinutes;
                  }
                ),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
          end_time: Yup.string().when('price_unit', {
            is: (value: string) => value === PriceUnit.PER_HOUR,
            then: (schema) =>
              schema
                .required('End time is required')
                .test(
                  'is-after-start-time',
                  'End time must be after start time',
                  function (endTime) {
                    const startTime = this.parent.start_time;
                    if (
                      !startTime ||
                      !endTime ||
                      this.parent.price_unit !== PriceUnit.PER_HOUR
                    ) {
                      return true;
                    }
                    // Convert time strings to minutes for proper comparison
                    const startMinutes = timeToMinutes(startTime);
                    const endMinutes = timeToMinutes(endTime);
                    return endMinutes > startMinutes;
                  }
                ),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
          transaction_presentation_mode: Yup.string().nullable().notRequired(),
          access_handover_mode: Yup.string()
            .oneOf(['NONE', 'KEY_BOX', 'ACCESS_CODE'])
            .notRequired(),
          key_box_code: Yup.string().when('access_handover_mode', {
            is: (m: string) => m === 'KEY_BOX' || m === 'ACCESS_CODE',
            then: (schema) =>
              schema.required('Key box code is required').trim(),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
          key_box_pickup_instructions: Yup.string().when(
            'access_handover_mode',
            {
              is: (m: string) => m === 'KEY_BOX' || m === 'ACCESS_CODE',
              then: (schema) =>
                schema
                  .required('Pickup / usage instructions are required')
                  .trim(),
              otherwise: (schema) => schema.notRequired().nullable(),
            }
          ),
          key_box_return_instructions: Yup.string().when(
            'access_handover_mode',
            {
              is: 'KEY_BOX',
              then: (schema) =>
                schema.required('Return instructions are required').trim(),
              otherwise: (schema) => schema.notRequired().nullable(),
            }
          ),
        });

        await schema.validateAt(
          name as string,
          { ...formData, [name]: value },
          { abortEarly: true }
        );
        setErrors((prev) => ({
          ...prev,
          [name]: '',
        }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => {
            const newError = err.errors[0] || 'Invalid input';
            if (prev[name] === newError) return prev;
            return { ...prev, [name]: newError };
          });
        }
      }
    },
    [setErrors, formData, timeToMinutes]
  );

  const { data: harborsData, isLoading: isHarborsLoading } = useGetHarborsQuery(
    {}
  );

  const selectedHarbor = useMemo(() => {
    return harborOptions.find(
      (h) => h.id === (formData.harbor_id || formData.harbor?.id)
    );
  }, [harborOptions, formData.harbor_id, formData.harbor?.id]);

  const shouldShowVatRegimeField =
    selectedHarbor?.countryShortCode?.toUpperCase() === 'FI';

  const { data: vatConfigsResponse, isLoading: isVatConfigsLoading } =
    useGetAddonVatRegimesQuery(undefined, {
      skip: !open || !shouldShowVatRegimeField,
    });

  const { data: selectedVatConfig } = useGetAddonVatRegimeByIdQuery(
    formData.product_vat_config_id as string,
    {
      skip:
        !open ||
        !shouldShowVatRegimeField ||
        !formData.product_vat_config_id ||
        (vatConfigsResponse?.data ?? []).some(
          (config) => config.id === formData.product_vat_config_id
        ),
    }
  );

  const vatRegimeOptions = useMemo(() => {
    const configs = [...(vatConfigsResponse?.data ?? [])];

    if (
      selectedVatConfig &&
      !configs.some((config) => config.id === selectedVatConfig.id)
    ) {
      configs.push(selectedVatConfig);
    }

    return configs
      .filter(
        (config: AddonVatRegime) =>
          isProductVatConfigActive(config) ||
          config.id === formData.product_vat_config_id
      )
      .map((config: AddonVatRegime) => ({
        value: config.id,
        label: `${config.default_vat_rate}% - Domestic only (Finland)${
          isProductVatConfigActive(config) ? '' : ' (Inactive)'
        }`,
      }));
  }, [
    formData.product_vat_config_id,
    selectedVatConfig,
    vatConfigsResponse?.data,
  ]);

  const hasSetHarbor = useRef(false);

  useEffect(() => {
    if (harborsData?.data && open && !hasSetHarbor.current) {
      const options = harborsData.data.map((harbor: HarborResponse) => ({
        id: harbor.id,
        name: harbor.name,
        countryShortCode: harbor.country_short_code,
      }));

      if (JSON.stringify(options) !== JSON.stringify(harborOptions)) {
        setHarborOptions(options);
      }

      if ((mode === 'edit' || mode === 'view') && formData.harbor?.id) {
        const selectedHarbor = harborsData.data.find(
          (harbor: HarborResponse) => harbor.id === formData.harbor?.id
        );

        if (selectedHarbor && formData.harbor_id !== selectedHarbor.id) {
          hasSetHarbor.current = true;
          setFormData((prev: Partial<IAddonData>) => ({
            ...prev,
            harbor: {
              id: selectedHarbor.id,
              name: selectedHarbor.name,
            },
            harbor_id: selectedHarbor.id,
          }));
          validateField('harbor_id', selectedHarbor.id);
        }
      }
    }
  }, [
    harborsData,
    mode,
    open,
    formData.harbor,
    formData.harbor_id,
    harborOptions,
    setFormData,
    validateField,
  ]);

  useEffect(() => {
    if (!open) {
      hasSetHarbor.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (open && mode === 'create') {
      setFormData((prev) => ({
        ...prev,
        price_unit: PriceUnit.PER_DAY,
        start_time: '',
        end_time: '',
      }));
      setFormDataSnapshot({});
      setTranslationsValid(false);
    }
  }, [open, mode, setFormData]);

  useEffect(() => {
    if (!open || mode === 'create') return;

    if (hasTranslations(formData.translations)) {
      setTranslationsValid(true);
      setFormDataSnapshot({
        name: formData.name,
        description: formData.description,
        access_handover_mode: formData.access_handover_mode,
        key_box_pickup_instructions: formData.key_box_pickup_instructions,
        key_box_return_instructions: formData.key_box_return_instructions,
      });
    } else {
      setTranslationsValid(false);
      setFormDataSnapshot({});
    }
  }, [
    open,
    mode,
    formData.id,
    formData.translations,
    formData.name,
    formData.description,
    formData.access_handover_mode,
    formData.key_box_pickup_instructions,
    formData.key_box_return_instructions,
  ]);

  useEffect(() => {
    if (!translationsValid) return;

    const hasChanged =
      formData.name !== formDataSnapshot.name ||
      formData.description !== formDataSnapshot.description ||
      formData.access_handover_mode !== formDataSnapshot.access_handover_mode ||
      formData.key_box_pickup_instructions !==
        formDataSnapshot.key_box_pickup_instructions ||
      formData.key_box_return_instructions !==
        formDataSnapshot.key_box_return_instructions;

    if (hasChanged) {
      setTranslationsValid(false);
    }
  }, [
    formData.name,
    formData.description,
    formData.access_handover_mode,
    formData.key_box_pickup_instructions,
    formData.key_box_return_instructions,
    formDataSnapshot,
    translationsValid,
  ]);

  useEffect(() => {
    if (
      !open ||
      !selectedHarbor ||
      shouldShowVatRegimeField ||
      formData.product_vat_config_id == null
    ) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      product_vat_config_id: null,
    }));
  }, [
    formData.product_vat_config_id,
    open,
    setFormData,
    shouldShowVatRegimeField,
    selectedHarbor,
  ]);

  // Focus the Addon Name field when modal opens (not in view mode)
  useEffect(() => {
    if (open && mode !== 'view') {
      setTimeout(() => nameInputRef.current?.focus(), 0);
    }
  }, [open, mode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        harborDropdownRef.current &&
        !harborDropdownRef.current.contains(event.target as Node)
      ) {
        setIsHarborDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value } = e.target;
      const key = name as keyof IAddonData;

      // For time inputs, ensure minutes are always :00
      let processedValue: string | null = value;
      if ((key === 'start_time' || key === 'end_time') && value) {
        const timeParts = value.split(':');
        if (timeParts.length >= 2) {
          processedValue = `${timeParts[0]}:00`;
        }
      }

      if (key === 'product_vat_config_id' && value === '') {
        processedValue = null;
      }

      if (key === 'transaction_presentation_mode' && value === '') {
        processedValue = null;
      }

      setFormData((prev: Partial<IAddonData>) => {
        const updatedData = {
          ...prev,
          [key]: processedValue,
          ...(key === 'price_unit' && processedValue === PriceUnit.PER_DAY
            ? { start_time: '', end_time: '' }
            : {}),
          ...(key === 'access_handover_mode' &&
          !addonUsesSecretHandover(processedValue as string)
            ? {
                key_box_code: '',
                key_box_pickup_instructions: '',
                key_box_return_instructions: '',
              }
            : {}),
        };
        return updatedData;
      });

      if (key !== 'price_unit') {
        validateField(key, processedValue);
      } else {
        validateField('start_time', formData.start_time);
        validateField('end_time', formData.end_time);
        if (processedValue === PriceUnit.PER_DAY) {
          setErrors((prev) => ({
            ...prev,
            start_time: '',
            end_time: '',
          }));
        }
      }
    },
    [
      setFormData,
      validateField,
      formData.start_time,
      formData.end_time,
      setErrors,
    ]
  );

  const handleInputBlur = useCallback(
    (name: string, value: unknown) => {
      if (name !== 'price_unit') {
        validateField(name, value);
      }
    },
    [validateField]
  );

  const handleHarborDropdownToggle = () => {
    if (mode !== 'view' && !isHarborsLoading && !isSubmitting) {
      setIsHarborDropdownOpen(!isHarborDropdownOpen);
    }
  };

  const handleAutotranslateAddon =
    useCallback(async (): Promise<AddonTranslationMap | null> => {
      if (hasTranslations(formData.translations)) {
        return formData.translations;
      }

      try {
        const res = await translateAddon({
          addon_id: formData.id,
          name: formData.name || '',
          description: formData.description || '',
          source_language: formData.language || 'en',
          access_handover_mode: formData.access_handover_mode,
          key_box_pickup_instructions: formData.key_box_pickup_instructions,
          key_box_return_instructions: formData.key_box_return_instructions,
        }).unwrap();
        if (res.status && res.data?.translations) {
          const generatedTranslations = res.data.translations;
          setFormData((prev) => ({
            ...prev,
            translations: generatedTranslations,
          }));
          setFormDataSnapshot({
            name: formData.name,
            description: formData.description,
            access_handover_mode: formData.access_handover_mode,
            key_box_pickup_instructions: formData.key_box_pickup_instructions,
            key_box_return_instructions: formData.key_box_return_instructions,
          });
          setTranslationsValid(true);
          
          const warningMessage = res?.warning_message;
          if (typeof warningMessage === 'string' && warningMessage.trim()) {
            enqueueSnackbar(warningMessage, { variant: 'warning' });
          } else {
            enqueueSnackbar(
              res?.message || intl.formatMessage({ id: 'ADDON.AUTOTRANSLATE_SUCCESS' }),
              { variant: 'success' }
            );
          }
          return generatedTranslations;
        }
        return null;
      } catch (error) {
        enqueueSnackbar(
          getApiErrorMessage(error) ||
            intl.formatMessage({ id: 'ADDON.AUTOTRANSLATE_ERROR' }),
          {
            variant: 'error',
          }
        );
        return null;
      }
    }, [
      translateAddon,
      formData.id,
      formData.name,
      formData.description,
      formData.language,
      formData.translations,
      formData.access_handover_mode,
      formData.key_box_pickup_instructions,
      formData.key_box_return_instructions,
      setFormData,
      intl,
    ]);

  useEffect(() => {
    const nameOk = String(formData.name || '').trim().length > 0;
    const hasErrors = Object.values(errors || {}).some(
      (v) => String(v || '').trim().length > 0
    );
    const modeIsEdit = mode === 'create' || mode === 'edit';
    setShowPreviewNotice(nameOk && !hasErrors && modeIsEdit);
  }, [formData.name, errors, mode]);

  const handleOpenPreview = useCallback(async () => {
    setIsPreviewModalOpen(true);
  }, []);

  const handleTranslationsUpdate = useCallback(
    (newTranslations: AddonTranslationMap) => {
      setFormData((prev) => ({ ...prev, translations: newTranslations }));
      setFormDataSnapshot({
        name: formData.name,
        description: formData.description,
        access_handover_mode: formData.access_handover_mode,
        key_box_pickup_instructions: formData.key_box_pickup_instructions,
        key_box_return_instructions: formData.key_box_return_instructions,
      });
      setTranslationsValid(true);
    },
    [
      setFormData,
      formData.name,
      formData.description,
      formData.access_handover_mode,
      formData.key_box_pickup_instructions,
      formData.key_box_return_instructions,
    ]
  );

  const handleHarborSelect = (harbor: HarborOption) => {
    setFormData((prev: Partial<IAddonData>) => ({
      ...prev,
      harbor_id: harbor.id,
      product_vat_config_id:
        harbor.countryShortCode?.toUpperCase() === 'FI'
          ? (prev.product_vat_config_id ?? null)
          : null,
      harbor: { id: harbor.id, name: harbor.name },
    }));
    validateField('harbor_id', harbor.id);
    setIsHarborDropdownOpen(false);
    setSearchTerm('');
  };

  const handleNumberInputFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const preventWheel = (event: WheelEvent) => event.preventDefault();
      e.target.addEventListener('wheel', preventWheel, {
        passive: false,
      });
      // Store the function reference on the element for later removal
      (e.target as HTMLInputElementWithPreventWheel)._preventWheel =
        preventWheel;
    },
    []
  );

  const handleNumberInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      try {
        // Remove the stored function reference
        const preventWheel = (e.target as HTMLInputElementWithPreventWheel)
          ._preventWheel;
        if (preventWheel) {
          e.target.removeEventListener('wheel', preventWheel);
          delete (e.target as HTMLInputElementWithPreventWheel)._preventWheel;
        }
      } catch {
        // Silently handle any errors in test environments
      }
      const { name, value } = e.target;
      const parsedValue = value === '' ? undefined : parseFloat(value);
      handleInputBlur(name, parsedValue);
    },
    [handleInputBlur]
  );

  const handleTimeInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow navigation keys, backspace, delete, tab, etc.
      const allowedKeys = [
        'Tab',
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
        'Enter',
        'Escape',
      ];

      // Allow numbers for hour input
      const isNumber = /^[0-9]$/.test(e.key);

      if (!allowedKeys.includes(e.key) && !isNumber) {
        e.preventDefault();
      }
    },
    []
  );

  const handleTimeInputFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Set cursor position to hour part when focusing
      setTimeout(() => {
        try {
          // Check if the element supports setSelectionRange and is properly focused
          if (e.target && typeof e.target.setSelectionRange === 'function') {
            e.target.setSelectionRange(0, 2);
          }
        } catch {
          // Silently handle InvalidStateError and other potential errors
          // This can happen in test environments or when the input is not properly focused
        }
      }, 0);
    },
    []
  );

  const handleSubmit = async () => {
    try {
      const availabilityError = validateAvailabilityDateRanges(
        formData.availability_date_ranges
      );
      if (availabilityError) {
        setErrors((prev) => ({
          ...prev,
          availability_date_ranges: availabilityError,
        }));
        enqueueSnackbar('Please fix the availability date ranges', {
          variant: 'error',
        });
        return;
      }

      const schema = addonValidationSchema.shape({
        start_time: Yup.string().when('price_unit', {
          is: (value: string) => value === PriceUnit.PER_HOUR,
          then: (s) =>
            s
              .required('Start time is required')
              .test(
                'is-before-end-time',
                'Start time must be before end time',
                function (startTime) {
                  const endTime = this.parent.end_time;
                  if (
                    !startTime ||
                    !endTime ||
                    this.parent.price_unit !== PriceUnit.PER_HOUR
                  ) {
                    return true;
                  }
                  const startMinutes = timeToMinutes(startTime);
                  const endMinutes = timeToMinutes(endTime);
                  return startMinutes < endMinutes;
                }
              ),
          otherwise: (s) => s.notRequired().nullable(),
        }),
        end_time: Yup.string().when('price_unit', {
          is: (value: string) => value === PriceUnit.PER_HOUR,
          then: (s) =>
            s
              .required('End time is required')
              .test(
                'is-after-start-time',
                'End time must be after start time',
                function (endTime) {
                  const startTime = this.parent.start_time;
                  if (
                    !startTime ||
                    !endTime ||
                    this.parent.price_unit !== PriceUnit.PER_HOUR
                  ) {
                    return true;
                  }
                  const startMinutes = timeToMinutes(startTime);
                  const endMinutes = timeToMinutes(endTime);
                  return endMinutes > startMinutes;
                }
              ),
          otherwise: (s) => s.notRequired().nullable(),
        }),
      });
      await schema.validate(formData, { abortEarly: false });
      setErrors({});

      // Ensure translations exist and seed the source-language row with canonical fields
      const srcLang = String(formData.language || 'en').toLowerCase();
      let translationsToUse: AddonTranslationMap = formData.translations || {};
      if (!hasTranslations(translationsToUse)) {
        const generatedTranslations = await handleAutotranslateAddon();
        translationsToUse = generatedTranslations || {};
      }
      const seeded = {
        ...translationsToUse,
        [srcLang]: {
          ...(translationsToUse[srcLang] || {}),
          name: String(formData.name || ''),
          description: String(formData.description || ''),
          ...(addonUsesSecretHandover(formData.access_handover_mode)
            ? {
                key_box_pickup_instructions: String(
                  formData.key_box_pickup_instructions || ''
                ),
                key_box_return_instructions: String(
                  formData.key_box_return_instructions || ''
                ),
              }
            : {}),
        },
      };
      setFormData((prev) => ({ ...prev, translations: seeded }));
      await new Promise((r) => setTimeout(r, 0));

      await onSubmit();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setErrors(newErrors);
        enqueueSnackbar('Please fix the form errors before submitting', {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('An error occurred while submitting the form', {
          variant: 'error',
        });
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx('!flex items-center justify-center', className)}
      aria-labelledby="addon-modal-title"
      aria-describedby="addon-modal-description"
      data-testid="addon-modal"
      role="dialog"
    >
      <div className="bg-gray-100 p-8 w-full max-w-2xl mx-4 rounded-xl shadow-2xl flex flex-col">
        <h1
          id="addon-modal-title"
          className="text-2xl font-semibold text-gray-800 mb-6"
        >
          {mode === 'create'
            ? 'Create Addon'
            : mode === 'edit'
              ? 'Edit Addon'
              : 'View Addon'}
        </h1>
        <div id="addon-modal-description" className="sr-only">
          {mode === 'create'
            ? 'Form to create a new addon'
            : mode === 'edit'
              ? 'Form to edit addon details'
              : 'View addon details'}
        </div>
        <div
          tabIndex={0}
          className="flex-1 overflow-y-auto max-h-[70vh] pr-2 custom-scrollbar"
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-6">
            {/* Harbor */}
            <div className="col-span-1">
              <span className="block text-sm font-medium text-gray-700 mb-2">
                Harbor
              </span>
              <div className="relative" ref={harborDropdownRef}>
                <div
                  className={clsx(
                    'input input-bordered w-full h-11 flex items-center justify-between',
                    {
                      'cursor-pointer':
                        !isSubmitting &&
                        mode !== 'view' &&
                        !isHarborDetailsRoute,
                      'cursor-not-allowed bg-gray-100 dark:bg-coal-600 text-gray-700 dark:text-gray-800':
                        isSubmitting || mode === 'view' || isHarborDetailsRoute,
                      'border-primary':
                        isHarborDropdownOpen &&
                        mode !== 'view' &&
                        !isHarborDetailsRoute,
                      'border-red-500':
                        mode === 'create' ? errors.harbor_id : errors.harbor_id,
                    }
                  )}
                  onClick={
                    isSubmitting || mode === 'view' || isHarborDetailsRoute
                      ? undefined
                      : handleHarborDropdownToggle
                  }
                  aria-label="Select harbor"
                  aria-invalid={
                    mode === 'create' ? !!errors.harbor_id : !!errors.harbor_id
                  }
                  aria-describedby={
                    errors.harbor_id ? 'harbor_id-error' : undefined
                  }
                  aria-haspopup="listbox"
                  aria-expanded={isHarborDropdownOpen}
                  aria-disabled={
                    isSubmitting || mode === 'view' || isHarborDetailsRoute
                  }
                  role="combobox"
                >
                  <div className="truncate">
                    {formData.harbor?.name ||
                      (isHarborsLoading
                        ? 'Loading harbors...'
                        : 'Select harbor')}
                  </div>
                  <div className="flex items-center">
                    <svg
                      className={clsx(
                        'w-4 h-4 text-gray-400 transition-transform duration-200',
                        isHarborDropdownOpen && 'transform rotate-180'
                      )}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
                {isHarborDropdownOpen &&
                  !isHarborsLoading &&
                  mode !== 'view' &&
                  !isHarborDetailsRoute && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      <div className="p-2 border-b">
                        <input
                          type="text"
                          placeholder="Search harbors..."
                          className="w-full p-2 border rounded"
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setIsHarborDropdownOpen(true);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {harborOptions.length > 0 ? (
                        harborOptions.map((harbor) => (
                          <div
                            key={harbor.id}
                            role="option"
                            aria-selected={formData.harbor_id === harbor.id}
                            className={clsx(
                              'px-4 py-2 cursor-pointer hover:bg-gray-100',
                              formData.harbor_id === harbor.id &&
                                'bg-primary-light'
                            )}
                            onClick={() => handleHarborSelect(harbor)}
                          >
                            {harbor.name}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-500">
                          {searchTerm && harborOptions.length === 0
                            ? 'Harbor not found'
                            : 'No harbors available'}
                        </div>
                      )}
                    </div>
                  )}
              </div>
              {mode === 'create'
                ? errors.harbor_id && (
                    <p
                      id="harbor_id-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.harbor_id}
                    </p>
                  )
                : errors.harbor_id && (
                    <p
                      id="harbor_id-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.harbor_id}
                    </p>
                  )}
            </div>

            {/* Name */}
            <div className="col-span-1">
              <label
                htmlFor="name-input"
                className="block text-sm font-medium text-gray-700 mb-2"
                aria-label="Addon Name"
              >
                Addon Name
              </label>
              <input
                id="name-input"
                type="text"
                name="name"
                data-testid="name-input"
                ref={nameInputRef}
                value={formData.name || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.name && 'border-red-500'
                )}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'name-error' : undefined}
                placeholder="Enter addon name"
                required
              />
              {errors.name && (
                <p
                  id="name-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.name}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="col-span-1 md:col-span-2">
              <label
                htmlFor="description-input"
                className="block text-sm font-medium text-gray-700 mb-2"
                aria-label="Description"
              >
                Description
              </label>
              <textarea
                id="description-input"
                name="description"
                value={formData.description || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  'textarea textarea-bordered w-full rounded-lg border-gray-300',
                  errors.description && 'border-red-500'
                )}
                aria-invalid={!!errors.description}
                aria-describedby={
                  errors.description ? 'description-error' : undefined
                }
                placeholder="Enter addon description"
                rows={4}
              />
              {errors.description && (
                <p
                  id="description-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.description}
                </p>
              )}
            </div>

            {/* Price */}
            <div className="col-span-1">
              <label
                htmlFor="price-input"
                className="block text-sm font-medium text-gray-700 mb-2"
                aria-label="Price"
              >
                Price
              </label>
              <PriceInput
                id="price-input"
                name="price"
                value={formData.price ?? ''}
                autoComplete="off"
                onChange={(val) => {
                  setFormData((prev) => ({
                    ...prev,
                    price: val,
                  }));
                  validateField('price', val);
                }}
                disabled={mode === 'view' || isSubmitting}
                inputClassName={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                  errors.price && 'border-red-500'
                )}
                error={errors.price}
                placeholder="Enter price"
              />
            </div>

            {shouldShowVatRegimeField && (
              <div className="col-span-1">
                <label
                  htmlFor="product_vat_config_id"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  VAT Regime
                </label>
                <select
                  id="product_vat_config_id"
                  name="product_vat_config_id"
                  value={formData.product_vat_config_id ?? ''}
                  onChange={handleInputChange}
                  disabled={
                    mode === 'view' || isSubmitting || isVatConfigsLoading
                  }
                  className="select select-bordered w-full h-11 rounded-lg border-gray-300"
                >
                  <option value="">Select VAT regime</option>
                  {vatRegimeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Price Unit */}
            <div>
              <label
                htmlFor="price_unit"
                className="block text-sm font-medium text-gray-700 mb-2"
                aria-label="Price Unit"
              >
                Price Unit
              </label>
              <select
                id="price_unit"
                name="price_unit"
                value={formData.price_unit || PriceUnit.PER_DAY}
                onChange={handleInputChange}
                className={clsx(
                  'select select-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.price_unit && 'border-red-500'
                )}
                disabled={mode === 'view' || isSubmitting}
                aria-invalid={!!errors.price_unit}
                aria-describedby={
                  errors.price_unit ? 'price_unit-error' : undefined
                }
              >
                <option value={PriceUnit.PER_DAY}>Per Day</option>
                <option value={PriceUnit.PER_HOUR}>Per Hour</option>
              </select>
              {errors.price_unit && (
                <p
                  id="price_unit-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.price_unit}
                </p>
              )}
            </div>

            {/* Quantity */}
            <div className="col-span-1">
              <label
                htmlFor="quantity-input"
                className="block text-sm font-medium text-gray-700 mb-2"
                aria-label="Quantity"
              >
                Quantity
              </label>
              <NumberInput
                id="quantity-input"
                name="quantity"
                value={formData.quantity ?? ''}
                onChange={handleInputChange}
                onFocus={handleNumberInputFocus}
                onBlur={handleNumberInputBlur}
                disabled={mode === 'view' || isSubmitting}
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                  errors.quantity && 'border-red-500'
                )}
                aria-invalid={!!errors.quantity}
                aria-describedby={
                  errors.quantity ? 'quantity-error' : undefined
                }
                placeholder="Enter quantity"
                min="0"
                step="1"
                required
              />
              {errors.quantity && (
                <p
                  id="quantity-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.quantity}
                </p>
              )}
            </div>

            <div className="col-span-1 md:col-span-2 border rounded-lg p-4 space-y-4">
              <h2 className="text-sm font-semibold text-gray-800">
                Transaction and access (optional)
              </h2>
              <p className="text-xs text-gray-500">
                {intl.formatMessage({ id: 'ADDON.TRANSACTION_ACCESS_HELP' })}
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Book / Pay presentation
                </label>
                <select
                  name="transaction_presentation_mode"
                  aria-label="Book / Pay presentation"
                  value={formData.transaction_presentation_mode ?? ''}
                  onChange={handleInputChange}
                  disabled={mode === 'view' || isSubmitting}
                  className="select select-bordered w-full h-11 rounded-lg border-gray-300"
                >
                  <option value="">Use harbour default</option>
                  <option value="RESERVATION">Book</option>
                  <option value="PAYMENT_ONLY">Pay</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {intl.formatMessage({
                    id: 'ADDON.ACCESS_HANDOVER.LABEL',
                    defaultMessage: 'Access handover',
                  })}
                </label>
                <select
                  name="access_handover_mode"
                  aria-label="Access handover"
                  value={formData.access_handover_mode ?? 'NONE'}
                  onChange={handleInputChange}
                  disabled={mode === 'view' || isSubmitting}
                  className="select select-bordered w-full h-11 rounded-lg border-gray-300"
                >
                  <option value="NONE">
                    {intl.formatMessage({ id: 'ADDON.ACCESS_HANDOVER.NONE' })}
                  </option>
                  <option value="KEY_BOX">
                    {intl.formatMessage({
                      id: 'ADDON.ACCESS_HANDOVER.KEY_BOX',
                    })}
                  </option>
                  <option value="ACCESS_CODE">
                    {intl.formatMessage({
                      id: 'ADDON.ACCESS_HANDOVER.ACCESS_CODE',
                    })}
                  </option>
                </select>
              </div>
              {mode !== 'view' && (
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {intl.formatMessage({ id: 'ADDON.SOURCE_LANGUAGE' })}
                    </label>
                    <select
                      name="language"
                      aria-label="Source language"
                      value={formData.language ?? 'en'}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="select select-bordered w-full h-11 rounded-lg border-gray-300"
                    >
                      {I18N_LANGUAGES.map((l) => (
                        <option key={l.code} value={l.code}>
                          {l.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              {addonUsesSecretHandover(formData.access_handover_mode) && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {intl.formatMessage({ id: 'ADDON.KEY_BOX_CODE_LABEL' })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="key_box_code"
                      aria-label={intl.formatMessage({ id: 'ADDON.KEY_BOX_CODE_LABEL' })}
                      value={formData.key_box_code ?? ''}
                      onChange={handleInputChange}
                      onBlur={(e) =>
                        handleInputBlur(e.target.name, e.target.value)
                      }
                      disabled={mode === 'view' || isSubmitting}
                      className="textarea textarea-bordered w-full min-h-[72px]"
                    />
                    {errors.key_box_code && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.key_box_code}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {intl.formatMessage({ id: 'ADDON.PICKUP_INSTRUCTIONS' })}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="key_box_pickup_instructions"
                      aria-label={intl.formatMessage({ id: 'ADDON.PICKUP_INSTRUCTIONS' })}
                      value={formData.key_box_pickup_instructions ?? ''}
                      onChange={handleInputChange}
                      onBlur={(e) =>
                        handleInputBlur(e.target.name, e.target.value)
                      }
                      disabled={mode === 'view' || isSubmitting}
                      className="textarea textarea-bordered w-full min-h-[88px]"
                    />
                    {errors.key_box_pickup_instructions && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.key_box_pickup_instructions}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {intl.formatMessage({ id: 'ADDON.RETURN_INSTRUCTIONS' })}{' '}
                      {formData.access_handover_mode === 'KEY_BOX' ? (
                        <span className="text-red-500">*</span>
                      ) : (
                        <span className="text-gray-400 text-xs font-normal">
                          ({intl.formatMessage({ id: 'ADDON.OPTIONAL' })})
                        </span>
                      )}
                    </label>
                    <textarea
                      name="key_box_return_instructions"
                      aria-label={intl.formatMessage({ id: 'ADDON.RETURN_INSTRUCTIONS' })}
                      value={formData.key_box_return_instructions ?? ''}
                      onChange={handleInputChange}
                      onBlur={(e) =>
                        handleInputBlur(e.target.name, e.target.value)
                      }
                      disabled={mode === 'view' || isSubmitting}
                      className="textarea textarea-bordered w-full min-h-[88px]"
                    />
                    {errors.key_box_return_instructions && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.key_box_return_instructions}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            {showPreviewNotice && (
              <div className="col-span-1 md:col-span-2 mt-2 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 border border-blue-200 dark:border-blue-800 contrast:border-blue-800 rounded-lg p-4 flex items-center justify-between gap-2">
                <div className="flex items-center">
                  <p className="text-blue-700 dark:text-blue-100 contrast:text-blue-100 text-sm">
                    {intl.formatMessage({
                      id: 'COMMON.TRANSLATION_PREVIEW_AVAILABLE',
                    })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleOpenPreview}
                  className="btn btn-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2"
                  aria-label={intl.formatMessage({ id: 'COMMON.SHOW_PREVIEW' })}
                  disabled={isSubmitting || isTranslatingAddon}
                >
                  {intl.formatMessage({ id: 'COMMON.SHOW_PREVIEW' })}
                </button>
              </div>
            )}

            {/* Condition */}
            <div className="col-span-1">
              <label
                htmlFor="condition"
                className="block text-sm font-medium text-gray-700 mb-2"
                aria-label="Condition"
              >
                Condition
              </label>
              <select
                id="condition"
                name="condition"
                value={formData.condition || FacilityCondition.GOOD}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                className={clsx(
                  'select select-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.condition && 'border-red-500'
                )}
                disabled={mode === 'view' || isSubmitting}
                aria-invalid={!!errors.condition}
                aria-describedby={
                  errors.condition ? 'condition-error' : undefined
                }
              >
                {amenityConditionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {intl.formatMessage({ id: option.label })}
                  </option>
                ))}
              </select>
              {errors.condition && (
                <p
                  id="condition-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.condition}
                </p>
              )}

              {/* Show condition last updated info in edit/view mode */}
              {(mode === 'edit' || mode === 'view') &&
                formData.condition_updated_at && (
                  <div className="mt-2 text-xs text-gray-500">
                    {intl.formatMessage({
                      id: 'HARBOR.FACILITY.CONDITION.LAST_UPDATED',
                    })}
                    :{' '}
                    {formatDisplayDate(new Date(formData.condition_updated_at))}{' '}
                    {new Date(
                      formData.condition_updated_at
                    ).toLocaleTimeString()}
                    {formData.conditionUpdatedByUser && (
                      <>
                        {' '}
                        {intl.formatMessage({
                          id: 'HARBOR.FACILITY.CONDITION.BY',
                        })}{' '}
                        <span className="font-medium">
                          {formData.conditionUpdatedByUser.first_name &&
                          formData.conditionUpdatedByUser.last_name
                            ? `${formData.conditionUpdatedByUser.first_name} ${formData.conditionUpdatedByUser.last_name}`
                            : formData.conditionUpdatedByUser.email}
                        </span>
                      </>
                    )}
                  </div>
                )}
            </div>

            {/* Addon Standalone */}
            <div className="col-span-1">
              <label
                htmlFor="is_standalone"
                className="block text-sm font-medium text-gray-700 mb-2"
                aria-label="Addon Standalone"
              >
                Addon Standalone
              </label>
              <select
                id="is_standalone"
                name="is_standalone"
                value={formData.is_standalone ? 'true' : 'false'}
                onChange={(e) => {
                  const value = e.target.value === 'true';
                  setFormData((prev) => ({
                    ...prev,
                    is_standalone: value,
                  }));
                }}
                className={clsx(
                  'select select-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.is_standalone && 'border-red-500'
                )}
                disabled={mode === 'view' || isSubmitting}
                aria-invalid={!!errors.is_standalone}
                aria-describedby={
                  errors.is_standalone ? 'is_standalone-error' : undefined
                }
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
              {errors.is_standalone && (
                <p
                  id="is_standalone-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.is_standalone}
                </p>
              )}
            </div>

            {/* Start/End Time */}
            {formData.price_unit === PriceUnit.PER_HOUR && (
              <div className="col-span-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="start_time"
                    className="block text-sm font-medium text-gray-700 mb-2"
                    aria-label="Start Time"
                  >
                    Start Time
                  </label>
                  <input
                    type="time"
                    id="start_time"
                    name="start_time"
                    value={formData.start_time || ''}
                    onChange={handleInputChange}
                    onBlur={(e) =>
                      handleInputBlur(e.target.name, e.target.value)
                    }
                    onKeyDown={handleTimeInputKeyDown}
                    onFocus={handleTimeInputFocus}
                    step="3600"
                    className={clsx(
                      'input input-bordered w-full h-11 rounded-lg border-gray-300',
                      errors.start_time && 'border-red-500'
                    )}
                    disabled={mode === 'view' || isSubmitting}
                    aria-invalid={!!errors.start_time}
                    aria-describedby={
                      errors.start_time ? 'start_time-error' : undefined
                    }
                    title="Select hour only (minutes will be set to :00)"
                    autoComplete="off"
                  />
                  {errors.start_time && (
                    <p
                      id="start_time-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.start_time}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="end_time"
                    className="block text-sm font-medium text-gray-700 mb-2"
                    aria-label="End Time"
                  >
                    End Time
                  </label>
                  <input
                    type="time"
                    id="end_time"
                    name="end_time"
                    value={formData.end_time || ''}
                    onChange={handleInputChange}
                    onBlur={(e) =>
                      handleInputBlur(e.target.name, e.target.value)
                    }
                    onKeyDown={handleTimeInputKeyDown}
                    onFocus={handleTimeInputFocus}
                    step="3600"
                    className={clsx(
                      'input input-bordered w-full h-11 rounded-lg border-gray-300',
                      errors.end_time && 'border-red-500'
                    )}
                    disabled={mode === 'view' || isSubmitting}
                    aria-invalid={!!errors.end_time}
                    aria-describedby={
                      errors.end_time ? 'end_time-error' : undefined
                    }
                    title="Select hour only (minutes will be set to :00)"
                    autoComplete="off"
                  />
                  {errors.end_time && (
                    <p
                      id="end_time-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {errors.end_time}
                    </p>
                  )}
                </div>
              </div>
            )}

            <AddonAvailabilityDateRanges
              ranges={formData.availability_date_ranges || []}
              onChange={(ranges) => {
                setFormData((prev) => ({
                  ...prev,
                  availability_date_ranges: ranges,
                }));
                const availabilityError = validateAvailabilityDateRanges(ranges);
                setErrors((prev) => ({
                  ...prev,
                  availability_date_ranges: availabilityError || '',
                }));
              }}
              disabled={mode === 'view' || isSubmitting}
              error={errors.availability_date_ranges}
            />
          </div>
        </div>
        {(mode === 'create' || mode === 'edit') && (
          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="btn btn-sm btn-ghost text-gray-600 bg-gray-200 hover:bg-gray-300 dark:bg-coal-300 dark:text-gray-800 dark:hover:bg-coal-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
              aria-label="Cancel"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="btn btn-sm btn-primary bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={mode === 'create' ? 'Create Addon' : 'Save Changes'}
              disabled={isSubmitting}
              data-testid="modal-submit-button"
            >
              {isSubmitting
                ? 'Saving...'
                : mode === 'create'
                  ? 'Create'
                  : 'Save'}
            </button>
          </div>
        )}
        {isPreviewModalOpen && (
          <AddonPreviewModal
            open={isPreviewModalOpen}
            onClose={() => setIsPreviewModalOpen(false)}
            formData={formData}
            source_language={String(formData.language || 'en')}
            initialTranslations={previewInitialTranslations}
            onTranslationsUpdate={handleTranslationsUpdate}
          />
        )}
      </div>
    </Modal>
  );
};

export { AddonModal };
